/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PLOTTER-DRUCK AUS DER APP — Typen und Grenzwerte
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der SiGe-Plan geht nicht über den Druckdialog des Browsers an den Plotter,
 * sondern als Auftrag in die Datenbank (`plot_auftraege`, SQL Nr. 157/158).
 * Ein Druck-Agent auf einem immer laufenden Büro-PC holt die Aufträge ab
 * (nur ausgehend, keine Portfreigabe) und druckt sie über den HP-Treiber.
 * Damit geht der Druck auch von zu Hause oder von der Baustelle.
 *
 * Gegen Doppeldruck stehen drei Riegel:
 *  1. Die Auftrags-ID entsteht im Client, bevor gesendet wird — ein
 *     Doppelklick schickt dieselbe ID, der zweite Eintrag wird verworfen.
 *  2. Der Agent holt in EINEM Datenbankschritt ab (`for update skip locked`);
 *     ein abgeholter Auftrag geht an niemanden sonst.
 *  3. Bleibt ein Auftrag nach dem Abholen hängen, wird er `unklar` — nie
 *     automatisch nachgedruckt. Der Mensch entscheidet.
 *
 * Die Grenzwerte stehen gleichlautend in `supabase/plot_auftraege_rpc.sql`.
 */

export type PlotStatus =
    | 'wartet'
    | 'druckt'
    | 'gedruckt'
    | 'fehler'
    | 'unklar'
    | 'abgebrochen'
    | 'abgelaufen';

export const PLOT_STATUS_WERTE: readonly PlotStatus[] = [
    'wartet', 'druckt', 'gedruckt', 'fehler', 'unklar', 'abgebrochen', 'abgelaufen',
];

/** Eine Zeile aus `plot_auftraege` — ohne `pdf_url`, die braucht nur der Agent. */
export interface PlotAuftrag {
    id: string;
    company_id: string;
    project_id: string | null;
    sige_projekt_id: string | null;
    titel: string;
    dateiname: string;
    pdf_pfad: string;
    pdf_bytes: number | null;
    breite_mm: number;
    hoehe_mm: number;
    kopien: number;
    kopien_fertig: number;
    status: PlotStatus;
    meldung: string | null;
    erstellt_von: string;
    erstellt_von_name: string | null;
    erstellt_am: string;
    abgeholt_am: string | null;
    lebenszeichen_am: string | null;
    fertig_am: string | null;
    agent_id: string | null;
    wiederholung_von: string | null;
}

/** Spaltenliste für das Lesen der Aufträge (PostgREST `select`). */
export const PLOT_AUFTRAG_SPALTEN =
    'id,company_id,project_id,sige_projekt_id,titel,dateiname,pdf_pfad,pdf_bytes,breite_mm,hoehe_mm,'
    + 'kopien,kopien_fertig,status,meldung,erstellt_von,erstellt_von_name,erstellt_am,abgeholt_am,'
    + 'lebenszeichen_am,fertig_am,agent_id,wiederholung_von';

/** Ein angemeldeter Druck-PC, wie ihn `plot_status()` liefert (nie mit Token). */
export interface PlotAgent {
    id: string;
    name: string;
    letzter_kontakt: string | null;
    info: Record<string, unknown> | null;
    gesperrt: boolean;
    erstellt_am: string;
}

/** Antwort von `plot_status()`. `jetzt` ist die Serveruhr. */
export interface PlotAnlageStatus {
    jetzt: string;
    company_id: string | null;
    agenten: PlotAgent[];
}

/**
 * Ist ein Druck-PC da, der Aufträge abholt?
 *  - `nicht_eingerichtet` SQL Nr. 157/158 fehlt in dieser Umgebung
 *  - `kein_agent`         kein (ungesperrter) Druck-PC angemeldet
 *  - `nie_verbunden`      angemeldet, hat sich aber noch nie gemeldet
 *  - `erreichbar`         letzter Kontakt ≤ PLOT_ERREICHBAR_SEK
 *  - `verzoegert`         letzter Kontakt ≤ PLOT_VERZOEGERT_MIN
 *  - `nicht_erreichbar`   älter — Aufträge warten, bis er wieder läuft
 */
export type PlotErreichbarkeitStufe =
    | 'nicht_eingerichtet'
    | 'kein_agent'
    | 'nie_verbunden'
    | 'erreichbar'
    | 'verzoegert'
    | 'nicht_erreichbar';

export interface PlotErreichbarkeit {
    stufe: PlotErreichbarkeitStufe;
    /** Satz für die Oberfläche, z. B. „Druck-PC „Büro“ erreichbar“. */
    text: string;
    agentName?: string;
    letzterKontakt?: string | null;
}

export type PlotTon = 'neutral' | 'laeuft' | 'ok' | 'warnung' | 'fehler';

export const PLOT_MAX_KOPIEN = 5;
export const PLOT_BUCKET = 'plot-auftraege';
/** Signierter Link: länger als der Ablauf wartender Aufträge (7 Tage). */
export const PLOT_LINK_GUELTIG_SEK = 8 * 24 * 3600;
export const PLOT_TIMEOUT_DRUCKT_MIN = 15;
export const PLOT_ABLAUF_WARTET_TAGE = 7;
export const PLOT_MAX_OFFEN_JE_NUTZER = 10;
/** Der Agent fragt alle 10 s; zwei Minuten Stille sind schon auffällig. */
export const PLOT_ERREICHBAR_SEK = 120;
export const PLOT_VERZOEGERT_MIN = 60;

/** Was beim Senden schiefgehen kann — `message` ist immer ein deutscher Satz für die Oberfläche. */
export type PlotFehlerCode =
    | 'nicht_eingerichtet'
    | 'netz'
    | 'nicht_angemeldet'
    | 'keine_firma'
    | 'kein_zugriff'
    | 'kopien'
    | 'zu_viele_offen'
    | 'nicht_mehr_wartend'
    | 'nicht_unklar'
    | 'nur_admin'
    | 'hochladen'
    /** Die eingelegte Papierrolle reicht für den Plan nicht (Nr. 170, Trigger auf `plot_auftraege`). */
    | 'rolle_reicht_nicht'
    /** Eingabe zu Rolle oder Druck-Nachtrag ungültig (Länge, Kopien, Datum, Bemerkung). */
    | 'angabe'
    | 'unbekannt';

export class PlotFehler extends Error {
    readonly code: PlotFehlerCode;
    constructor(code: PlotFehlerCode, message: string) {
        super(message);
        this.name = 'PlotFehler';
        this.code = code;
    }
}

/**
 * Was der Plotter-Dialog druckt: ein SiGe-Plan oder eine Vorankündigung.
 * `schluessel` bündelt Sendeabsichten und Auftragsliste eines Dokuments —
 * SiGe-Plan: `sige_projekte.id`, Vorankündigung: `vorank:<projects.id>`.
 */
export interface PlotDokument {
    schluessel: string;
    projectId: string | null;
    sigeProjektId: string | null;
    titel: string;
    dateiname: string;
    /**
     * Welche Aufträge die Liste im Dialog zeigt: die des SiGe-Plans, oder die
     * des Projekts ohne SiGe-Plan (die Vorankündigung).
     */
    liste: { sigeProjektId: string } | { projectId: string; ohneSigePlan: true };
}

export interface PlotSendenEingabe {
    /** Einmal je Sendeabsicht erzeugt — ein zweiter Klick schickt dieselbe ID. */
    auftragId: string;
    blob: Blob;
    projectId: string | null;
    sigeProjektId: string | null;
    titel: string;
    dateiname: string;
    breiteMm: number;
    hoeheMm: number;
    kopien: number;
}

export interface PlotAgentNeu {
    id: string;
    name: string;
    /** Klartext — nur in dieser Antwort, danach nie wieder abrufbar. */
    token: string;
}
